import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  contentChildren,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  PLATFORM_ID,
  Signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { IconComponent } from '@libs/components/icons';
import { Subject, throttleTime } from 'rxjs';
import Swiper from 'swiper';
import { Autoplay, Manipulation } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import { SliderFactory } from './slider-factory';
import { SliderSlideDirective } from './slider-slide.directive';

@Component({
  selector: 'ui-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  providers: [SliderFactory],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    IconComponent,
  ],
})
export class SliderComponent implements AfterViewInit, OnDestroy {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly factory: SliderFactory = inject(SliderFactory);
  private readonly platformId: string = inject<string>(PLATFORM_ID);

  public readonly autoplay: InputSignal<boolean> = input(false);
  public readonly loop: InputSignal<boolean> = input(true);
  public readonly navigation: InputSignal<boolean> = input(false);
  public readonly slidesPerView: InputSignal<number | 'auto'> = input<number | 'auto'>('auto');
  public readonly speed: InputSignal<number> = input(1000);
  public readonly duration: InputSignal<number> = input(3000);
  public readonly spaceBetween: InputSignal<number> = input(0);
  public readonly responsive: InputSignal<boolean> = input(false);

  public readonly swiperContainerRef: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef>('swiperContainer');
  public readonly slides: Signal<readonly ElementRef<HTMLElement>[]> = contentChildren(SliderSlideDirective, { read: ElementRef });

  public readonly slideNext$: Subject<void> = new Subject<void>();
  public readonly slidePrevious$: Subject<void> = new Subject<void>();

  public swiper?: Swiper;

  public ngAfterViewInit(): void {
    const swiperElement: HTMLElement = this.swiperContainerRef().nativeElement;

    if (this.responsive()) {
      swiperElement.classList.add('responsive');
    }

    if (isPlatformBrowser(this.platformId)) {
      this.initializeSlider();
    }
  }

  public ngOnDestroy(): void {
    this.swiper?.destroy();
  }

  private initializeSlider(): void {
    this.swiper = this.factory.createSwiper(
      this.swiperContainerRef().nativeElement,
      this.slides().map(slide => slide.nativeElement),
      this.formatSwiperOptions(),
    );

    // If the user spams the buttons the slider breaks so throttle the input
    this.slideNext$.pipe(
      throttleTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.swiper?.slideNext();
    });

    this.slidePrevious$.pipe(
      throttleTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.swiper?.slidePrev();
    });
  }

  private formatSwiperOptions(): SwiperOptions {
    const options: SwiperOptions = {
      loop: this.loop(),
      speed: this.speed(),
      slidesPerView: this.slidesPerView(),
      spaceBetween: this.spaceBetween(),
      navigation: this.navigation(),
      updateOnWindowResize: true,
    };

    options.modules = [Manipulation];

    if (this.autoplay()) {
      options.modules?.push(Autoplay);
      options.autoplay = {
        delay: this.duration(),
        pauseOnMouseEnter: true,
        disableOnInteraction: true,
      };
    }

    return options;
  }
}
